<script setup lang="ts">
import { computed, ref } from 'vue';
import { ClassStudentInfo, ClassViewType } from '../../../types/class';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import AllowSpeakingStudents from './AllowSpeakingStudents.vue';
import AllowSpeakingGroups from './AllowSpeakingGroups.vue';
import { useClassStore, useSessionStore, useSignalRStore } from '../../../stores';
import Button from '../../General/Button.vue';
import CheckBox from '../../Form/CheckBox.vue';

const sessionStore = useSessionStore()
const classStore = useClassStore()
const signalr = useSignalRStore()

const emit = defineEmits<{ update: [] }>()

const currentViewType = ref<ClassViewType>(ClassViewType.student)
const allowSpeakingStudentIds = ref<number[]>(JSON.parse(JSON.stringify(sessionStore.allowSpeakingStudentIds || [])))
const isFollowTeacher = ref<true | null>(sessionStore.teacherVocabId ? true : null)
const studentsWithGroup = computed<ClassStudentInfo[]>(() => classStore.selectedClassDetails?.students?.filter(student => student.group_id) || [])
const isSelectedAll = computed<true | null>({
    get() {
        return studentsWithGroup.value.every(student => allowSpeakingStudentIds.value.includes(student.id)) || null
    },
    set(selected) {
        if (!selected) {
            allowSpeakingStudentIds.value = []
            return
        }
        allowSpeakingStudentIds.value = studentsWithGroup.value.map(student => student.id)
    }
})

function handleChangeViewType(viewType: ClassViewType) {
    currentViewType.value = viewType
}

function handleStopAll() {
    allowSpeakingStudentIds.value = []
    isFollowTeacher.value = null
    handleApply()
}

function handleApply() {
    signalr.allowSpeaking(allowSpeakingStudentIds.value, isFollowTeacher.value)
    emit('update')
}

const { t } = useI18n()
</script>

<template>
    <div class="flex items-center justify-center mb-3">
        <div class="flex items-center bg-sky-400 rounded-lg">
            <template v-for="viewType in ClassViewType">
                <Button v-if="viewType !== ClassViewType.leaderboard" :shadow="false"
                    :color="viewType === currentViewType ? 'secondary' : 'info'" class="border-none"
                    @click="handleChangeViewType(viewType)">
                    <span v-if="viewType === currentViewType" class="size-2 rounded-full bg-white"></span>
                    {{ t(TEXT[viewType]) }}
                </Button>
            </template>
        </div>
    </div>
    <div class="flex items-center justify-between px-3 py-1 border-2 border-transparent">
        <span class="text-blue-600 font-bold text-xl">{{ t(TEXT.select_all) }}</span>
        <CheckBox v-model="isSelectedAll" :value="true" />
    </div>
    <div class="flex flex-col flex-1 border-2 border-gray-200 bg-white rounded-lg overflow-auto">
        <AllowSpeakingStudents v-if="currentViewType === ClassViewType.student" v-model="allowSpeakingStudentIds" />
        <AllowSpeakingGroups v-if="currentViewType === ClassViewType.group" v-model="allowSpeakingStudentIds" />
    </div>
    <div class="my-5 mx-auto">
        <label class="flex gap-2 cursor-pointer">
            <span class="font-bold underline text-blue-600">{{ t(TEXT.follow_teacher_screen) }}</span>
            <CheckBox v-model="isFollowTeacher" :value="true" />
        </label>
    </div>
    <div class="grid grid-cols-2 gap-3">
        <Button color="secondary" @click="handleStopAll">{{ t(TEXT.stop_all) }}</Button>
        <Button color="success" @click="handleApply">{{ t(TEXT.apply) }}</Button>
    </div>
</template>